/**
 * NEXT_PUBLIC_FRONTEND_URI may list multiple comma-separated origins (CORS).
 * Use a single canonical origin for absolute URLs (share links, OAuth, etc.).
 */
export function primaryFrontendOrigin(raw?: string | null): string {
	const value = (raw ?? "").trim();
	if (!value) return "http://localhost:3000";

	const segments = value
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);

	const toOrigin = (segment: string): string | null => {
		const candidate = /^https?:\/\//i.test(segment)
			? segment
			: `https://${segment.replace(/^\/+/, "")}`;
		try {
			return new URL(candidate).origin.replace(/\/+$/, "");
		} catch {
			return null;
		}
	};

	for (const segment of segments) {
		if (/^https:\/\//i.test(segment)) {
			const origin = toOrigin(segment);
			if (origin) return origin;
		}
	}

	for (const segment of segments) {
		const origin = toOrigin(segment);
		if (origin) return origin;
	}

	return "http://localhost:3000";
}

export function buildFrontendUrl(path: string, raw?: string | null): string {
	const base = primaryFrontendOrigin(
		raw === undefined ? process.env.NEXT_PUBLIC_FRONTEND_URI : raw,
	);
	if (!path) return base;
	const normalized = path.startsWith("/") ? path : `/${path}`;
	return `${base}${normalized}`;
}

/** Absolute URL on the host that received this request (needed for Apple form_post). */
export function buildRequestAbsoluteUrl(requestUrl: string, path: string): string {
	const normalized = path.startsWith("/") ? path : `/${path}`;
	return new URL(normalized, requestUrl).toString();
}

/** Env-based marketplace origin for client-side absolute URLs. */
export function getConfiguredFrontendOrigin(): string {
	return primaryFrontendOrigin(process.env.NEXT_PUBLIC_FRONTEND_URI);
}

/**
 * Origin for OAuth redirect_uri values.
 * Prefer the configured www origin so apex (techxeed.com) and www both
 * hit the same Google/Apple-registered callback URLs.
 */
export function getClientFrontendOrigin(): string {
	const configured = getConfiguredFrontendOrigin();
	if (configured && !configured.includes("localhost")) {
		return configured;
	}
	if (typeof window !== "undefined") {
		return window.location.origin.replace(/\/+$/, "");
	}
	return configured;
}
